import {
  Component,
  ElementRef,
  HostListener,
  ContentChildren,
  ContentChild,
  QueryList,
  ViewChild,
  EventEmitter,
  Input,
  AfterViewInit
} from "@angular/core";
import { ActivatedRoute, PRIMARY_OUTLET } from "@angular/router";

import * as _ from "lodash";
import { DeviceDetectorService, DeviceInfo } from "ngx-device-detector";

import { ConfigService } from "../../config.service";

import { ActionBarComponent } from "./components/action-bar.component";
import { FormBarComponent } from "./components/form-bar.component";

import { TemplateSize, TemplateTheme } from "./template.enum";
import { LayoutRouterService } from "../layout/services/router.service";

const ROUTE_DATA_BREADCRUMB = "$title";

@Component({
  selector: "uex-template",
  templateUrl: "./template.component.html",
  host: {
    class: "uex-template",
    "[attr.title]": "null",
    "[attr.subtitle]": "null"
  }
})
export class TemplateComponent implements AfterViewInit {
  @ContentChildren(ActionBarComponent, { descendants: true })
  private set _actionBars(action_bars: QueryList<ActionBarComponent>) {
    setTimeout(() => (this.actionBars = action_bars.toArray()), 0);
  }
  @ContentChild(FormBarComponent, { static: false }) private set _formBar(
    form_bar: FormBarComponent
  ) {
    setTimeout(() => (this.formBar = form_bar), 0);
  }

  constructor(
    private _route: ActivatedRoute,
    private _deviceService: DeviceDetectorService,
    private _config: ConfigService,
    private _layoutRouterService: LayoutRouterService,
    public elementRef: ElementRef
  ) {}

  @ViewChild("wrapper", { static: true }) private _wrapper: ElementRef;
  @ViewChild("header", { static: false }) private _header: ElementRef;
  @ViewChild("content", { static: true }) private _content: ElementRef;

  @Input("title") title: string;
  @Input("subtitle") subtitle: string;
  @Input("type") type = "default";
  @Input("show-header") show_header: boolean;
  @Input("show-layout-header") show_layout_header: boolean;
  @Input("show-layout-sidebar") show_layout_sidebar: boolean;
  @Input("size") size: TemplateSize = TemplateSize.RESPONSIVE;

  public formBar: FormBarComponent;
  public actionBars: Array<ActionBarComponent>;

  public onSizeChange: EventEmitter<TemplateSizes> = new EventEmitter();
  public onScroll: EventEmitter<TemplateScrollRef> = new EventEmitter();

  public breakPoint: TemplateBreakPoint;
  public breakPoints: Array<TemplateBreakPoint> = [
    "xs",
    "sm",
    "md",
    "lg",
    "xl"
  ];

  public sizes: TemplateSizes = {
    width: 0,
    height: 0,
    header_height: 0,
    offset_height: 0
  };

  public scrollRef: TemplateScrollRef = {
    top: 0,
    left: 0
  };

  public relative = false;

  private _device_info: DeviceInfo = this._deviceService.getDeviceInfo();

  @HostListener("window:resize") public _onResize() {
    this.resize();
  }

  @HostListener("scroll") public _onScroll() {
    this.scrollRef = {
      top: this._getScrollElement().scrollTop,
      left: this._getScrollElement().scrollLeft
    };
    this.onScroll.emit(this.scrollRef);
  }

  public ngOnInit() {
    this._layoutRouterService.registerTemplate(this);
  }

  public ngOnDestroy() {
    this._layoutRouterService.unregisterTemplate();
  }

  public ngAfterViewInit() {
    setTimeout(() => {
      if (!this.title) {
        const root: ActivatedRoute = this._route.root;
        this.title = this._getActiveRoute(root).snapshot.data[
          ROUTE_DATA_BREADCRUMB
        ];
      }
      this.resize();
      this._updateData();
    }, 0);
  }

  public ngOnChanges() {
    this._updateData();
  }

  public scrollTo(offset: number, smooth: boolean = false) {
    if (this.hasScroll()) {
      this._getScrollElement().scroll({
        top: offset,
        behavior: smooth ? "smooth" : "auto"
      });
    }
  }

  public scrollToAnchor(anchor: string, smooth: boolean = false) {
    if (this.hasScroll()) {
      if (anchor.indexOf("#") !== 0) {
        anchor = `#${anchor}`;
      }
      const el = this.elementRef.nativeElement.querySelector(anchor);
      if (!el) {
        return;
      }
      this.scrollTo((<any>el).offsetTop, smooth);
    }
  }

  public scrollToTop(smooth: boolean = false) {
    if (this.hasScroll()) {
      this._getScrollElement().scroll({
        top: 0,
        behavior: smooth ? "smooth" : "auto"
      });
    }
  }

  public scrollToBottom(smooth: boolean = false) {
    if (this.hasScroll()) {
      this._getScrollElement().scroll({
        top: this._getScrollElement().scrollHeight,
        behavior: smooth ? "smooth" : "auto"
      });
    }
  }

  public resize() {
    this._setBreakPoint();
    this._setSizes();
  }

  public isBreakPoint(size: TemplateBreakPoint): boolean {
    return this.breakPoint == size;
  }

  public isBreakPointMin(size: TemplateBreakPoint): boolean {
    return (
      this.breakPoints.indexOf(size) <=
      this.breakPoints.indexOf(this.breakPoint)
    );
  }

  public isBreakPointMax(size: TemplateBreakPoint): boolean {
    return (
      this.breakPoints.indexOf(size) >=
      this.breakPoints.indexOf(this.breakPoint)
    );
  }

  public getConfig(path): any {
    if (!this.type) {
      this.type = "default";
    }
    if (this._config) {
      const template_type =
        this._config.template[this.type] ||
        this._config.template["1"] ||
        this._config.template.default;

      if (!template_type) return null;

      switch (path) {
        case "background.color":
          return template_type.background.color;
        case "background.image":
          return template_type.background.image
            ? `url("${template_type.background.image}")`
            : null;
        default:
          return _.get(template_type, path);
      }
    } else {
      return null;
    }
  }

  public isTheme(theme_type: string): boolean {
    return this.getConfig("theme") == TemplateTheme[theme_type];
  }

  public hasFormBar(): boolean {
    // return this.has_form_bar;
    return this.formBar && this.formBar.content ? true : false;
  }

  public hasActionBar(): boolean {
    return this.actionBars && this.actionBars.length > 0;
  }

  public hasScroll(): boolean {
    if (this._wrapper) {
      const el = this._getScrollElement(true);
      // const wrapper = this._wrapper.nativeElement.childNodes[0];
      const wrapper = this._wrapper.nativeElement;
      return wrapper.offsetHeight > el.offsetHeight;
    }
    return false;
  }

  private _getScrollElement(use_body: boolean = false): HTMLElement {
    const is_safari = this._device_info.browser == "safari";
    return this._config.layout.relative
      ? use_body || is_safari
        ? document.body
        : document.documentElement
      : this.elementRef.nativeElement;
  }

  private _setBreakPoint() {
    const width = document.body.clientWidth;
    switch (true) {
      case width < 576:
        this.breakPoint = "xs";
        break;
      case width >= 576 && width < 768:
        this.breakPoint = "sm";
        break;
      case width >= 768 && width < 992:
        this.breakPoint = "md";
        break;
      case width >= 992 && width < 1200:
        this.breakPoint = "lg";
        break;
      case width > 1200:
        this.breakPoint = "xl";
        break;
    }
  }

  private _setSizes() {
    setTimeout(() => {
      this.sizes.width = this._getScrollElement().clientWidth;
      this.sizes.height = this._getScrollElement().offsetHeight;
      this.sizes.header_height = this._header
        ? this._header.nativeElement.offsetHeight
        : 0;
      let content_padding = 0;
      if (this._content) {
        const content_styles = window.getComputedStyle(
          this._content.nativeElement
        );
        const content_padding_top = parseFloat(
          content_styles.getPropertyValue("padding-top")
        );
        const content_padding_bottom = parseFloat(
          content_styles.getPropertyValue("padding-bottom")
        );
        content_padding = content_padding_top + content_padding_bottom;
      }
      const offset = this.sizes.height - this.sizes.header_height;
      this.sizes.offset_height = offset - content_padding;
      this.onSizeChange.emit(this.sizes);
    }, 100);
  }

  private _updateData() {
    if (_.isUndefined(this.show_header)) {
      const show_header = this.getConfig("header");
      this.show_header = _.isBoolean(show_header) ? show_header : true;
    }
    if (_.isUndefined(this.show_layout_header)) {
      const show_layout_header = this.getConfig("layout.header");
      this.show_layout_header = _.isBoolean(show_layout_header)
        ? show_layout_header
        : true;
    }
    if (_.isUndefined(this.show_layout_sidebar)) {
      const show_layout_sidebar = this.getConfig("layout.sidebar");
      this.show_layout_sidebar = _.isBoolean(show_layout_sidebar)
        ? show_layout_sidebar
        : true;
    }

    this._layoutRouterService.updateTemplate();
  }

  private _getActiveRoute(route: ActivatedRoute): ActivatedRoute {
    // Fet the child routes
    const children: ActivatedRoute[] = route.children;
    let current: ActivatedRoute;

    // Return if there are no more children
    if (children.length === 0) {
      return route;
    }

    // Iterate over each children
    for (const child of children) {
      // Verify primary route
      if (child.outlet !== PRIMARY_OUTLET) {
        continue;
      }

      // Recursive
      current = this._getActiveRoute(child);
    }

    return current;
  }
}

export type TemplateBreakPoint = "xs" | "sm" | "md" | "lg" | "xl";

export interface TemplateScrollRef {
  top: number;
  left: number;
}

export interface TemplateSizes {
  width: number;
  height: number;
  header_height: number;
  offset_height: number;
}
